import { ReleaseNote } from '@/api/apis/release-notes';
import { formatDate } from '@/utils/helpers/date.helpers';
import { Platform, PLATFORM_LABELS, PLATFORM_PRIORITY, ReleaseNoteProductConfig } from './constants';
import { GroupedReleases, SemanticVersion } from './types';

export const getPlatformLabel = (platform: string): string => PLATFORM_LABELS[platform.toLowerCase()] ?? platform;

export const formatReleaseDate = (dateString: string): string =>
  formatDate(dateString, 'MMMM d, yyyy', { placeholder: dateString });

const parseVersion = (version: string): SemanticVersion => {
  const parts = version.split('.').map((part) => parseInt(part, 10) || 0);
  return {
    major: parts[0] ?? 0,
    minor: parts[1] ?? 0,
    patch: parts[2] ?? 0,
  };
};

export const sortByVersionDesc = (a: ReleaseNote, b: ReleaseNote): number => {
  const versionA = parseVersion(a.version);
  const versionB = parseVersion(b.version);

  if (versionB.major !== versionA.major) {
    return versionB.major - versionA.major;
  }

  if (versionB.minor !== versionA.minor) {
    return versionB.minor - versionA.minor;
  }

  return versionB.patch - versionA.patch;
};

export const groupReleasesByPlatform = (releases: ReleaseNote[]): GroupedReleases => {
  const grouped = releases.reduce<GroupedReleases>((acc, release) => {
    const platform = release.platform.toLowerCase();
    if (!acc[platform]) {
      acc[platform] = [];
    }
    acc[platform].push(release);
    return acc;
  }, {});

  Object.keys(grouped).forEach((platform) => {
    grouped[platform].sort(sortByVersionDesc);
  });

  return grouped;
};

const getPlatformRank = (platform: string): number => {
  const rank = PLATFORM_PRIORITY.indexOf(platform.toLowerCase() as Platform);
  return rank === -1 ? PLATFORM_PRIORITY.length : rank;
};

export const sortPlatformKeys = (keys: string[]): string[] =>
  [...keys].sort((a, b) => getPlatformRank(a) - getPlatformRank(b) || a.localeCompare(b));

export const getPrimaryPlatform = (platforms: Platform[]): Platform | null =>
  (sortPlatformKeys(platforms)[0] as Platform) ?? null;

export const getPlatformOptions = (productConfig: ReleaseNoteProductConfig) => {
  return productConfig.platforms.map((platform) => ({
    key: platform,
    label: PLATFORM_LABELS[platform],
  }));
};
